import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { supabase } from '../supabaseClient';

const CheckoutContainer = styled.div`
  background: #1a1a3d;
  border-radius: 12px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  margin: 20px auto;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #e0e7ff;
  margin-bottom: 20px;
`;

const CryptoAddresses = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CopyBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: #e0e7ff;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  word-break: break-all;
  overflow-wrap: break-word;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CopyLabel = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
  color: #a0d8ff;
`;

const CopyFeedback = styled.span`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: #28a745;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
  text-align: left;
`;

const Label = styled.label`
  display: block;
  font-size: 1rem;
  margin-bottom: 5px;
  color: #ccc;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: #2a2a2a;
  color: #fff;
  font-size: 1rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: #2a2a2a;
  color: #fff;
  font-size: 1rem;
`;

const Button = styled.button`
  background: #28a745;
  color: #fff;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin: 20px 0 10px;
`;

const Message = styled.p`
  font-size: 1.1rem;
  margin: 20px 0;
  color: ${(props) => (props.error ? '#dc3545' : '#28a745')};
  display: ${(props) => (props.show ? 'block' : 'none')};
`;

const ContactInfo = styled.p`
  margin-top: 20px;
  color: #a0d8ff;
  font-size: 1.1rem;
  font-weight: 500;
`;

const CopyableAddress = ({ label, address }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <CopyBox onClick={handleCopy}>
      <CopyLabel>{label}:</CopyLabel>
      <div>{address}</div>
      <CopyFeedback show={copied}>Copied!</CopyFeedback>
    </CopyBox>
  );
};

const Checkout = () => {
  const [cryptoType, setCryptoType] = useState('');
  const [transactionHash, setTransactionHash] = useState('');
  const [platform, setPlatform] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const paymentDetails = JSON.parse(localStorage.getItem('paymentDetails') || '{}');
  const { serviceName = 'Unknown', amount = 0, method = 'crypto', service = '' } = paymentDetails;

  const lastSubmitRef = useRef(0);

  const cryptoAddresses = {
    ethereum: process.env.REACT_APP_ETH_ADDR || '0x3636ddB48C5b4B924638c9173764Ad84DaDEC234',
    solana: process.env.REACT_APP_SOL_ADDR || 'CG9shmbqgZKHTGJdPm25rQ2BQUgR6VKfCuALzAdTgxto',
    kaspa: process.env.REACT_APP_KAS_ADDR || 'kaspa:qyp60g7z60kk77vrjm2muz5knlex9uxlp88r2sznwsl30mxzrxwp2cglt7f5czn',
  };

  const sendNotificationEmail = async (data) => {
    const formData = new FormData();
    formData.append('service_name', data.service_name);
    formData.append('amount', data.amount);
    formData.append('crypto_type', data.crypto_type);
    formData.append('transaction_hash', data.transaction_hash);
    formData.append('email', data.email_add);
    formData.append('platform', data.platform);
    formData.append('username', data.username);
    formData.append('_subject', `New Crypto Payment - ${data.service_name}`);
    formData.append('_replyto', data.email_add);
    formData.append('_captcha', 'false');
    formData.append('_autoresponse', 'Thank you for your payment! We will contact you shortly.');

    await fetch(`https://formsubmit.co/${process.env.REACT_APP_CONTACT_EMAIL || 'Modmedia@lavabit.com'}`, {
      method: 'POST',
      body: formData,
    });
  };

  const CANONICAL_PRICES = {
    'website': 200,
    'ad-design': 10,
    'subscribe-card': 5,
    'comic_pay': 15,
  };

  const TX_PATTERNS = {
    ethereum: /^0x[a-fA-F0-9]{64}$/,
    solana: /^[1-9A-HJ-NP-Za-km-z]{87,88}$/,
    kaspa: /^[a-f0-9]{64}$/i,
  };

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const validateInputs = () => {
    if (!EMAIL_PATTERN.test(email)) return 'Invalid email address.';
    if (username.length < 1 || username.length > 64) return 'Username must be 1–64 characters.';
    if (transactionHash.length < 10 || transactionHash.length > 300) return 'Invalid transaction hash length.';
    if (TX_PATTERNS[cryptoType] && !TX_PATTERNS[cryptoType].test(transactionHash)) {
      return `Invalid ${cryptoType} transaction hash format.`;
    }
    return null;
  };

  const handleCryptoSubmit = async (e) => {
    e.preventDefault();

    // Rate limit: one submission per minute
    const now = Date.now();
    if (now - lastSubmitRef.current < 60000) {
      setErrorMessage('Please wait before submitting again.');
      return;
    }

    // Validate price against canonical server-side map (VUL-004)
    const expectedPrice = CANONICAL_PRICES[service];
    if (expectedPrice !== undefined && parseFloat(amount) !== expectedPrice) {
      setErrorMessage('Payment amount mismatch. Please restart the payment flow.');
      return;
    }

    // Validate inputs (VUL-005)
    const validationError = validateInputs();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsLoading(true);
    lastSubmitRef.current = now;

    try {
      const payload = {
        transaction_hash: transactionHash.trim(),
        platform,
        username: username.trim().slice(0, 64),
        email_add: email.trim().toLowerCase(),
        crypto_address: cryptoAddresses[cryptoType],
        crypto_type: cryptoType,
        service_name: serviceName,
        amount: expectedPrice ?? parseFloat(amount),
        payment_method: 'crypto',
      };

      const { error } = await supabase.from('services').insert(payload);
      if (error) throw error;

      await sendNotificationEmail(payload);

      setSuccessMessage(true);
      setErrorMessage('');
      setCryptoType('');
      setTransactionHash('');
      setPlatform('');
      setUsername('');
      setEmail('');
      localStorage.removeItem('paymentDetails');

      setTimeout(() => {
        window.location.href = '/';
      }, 5000);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Payment error:', error);
      setErrorMessage('Error processing payment. Please try again.');
      setSuccessMessage(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardPayment = () => {
    setIsLoading(true);
    let redirectUrl;
    if (service === 'subscribe-card' || service === 'ad-design') redirectUrl = 'https://modmedianow.gumroad.com/l/buy/';
    else if (service === 'website') redirectUrl = 'https://modmedianow.gumroad.com/l/webdev/';
    else if (service === 'comic_pay') redirectUrl = 'https://modmedianow.gumroad.com/l/comic/';
    else redirectUrl = 'https://modmedianow.gumroad.com';
    window.location.href = redirectUrl;
  };

  return (
    <CheckoutContainer>
      <Title>ModMedia Payment</Title>

      {method === 'crypto' ? (
        <>
          <p>Please send your payment to one of the following cryptocurrency addresses:</p>
          <CryptoAddresses>
            <CopyableAddress label="Ethereum" address={cryptoAddresses.ethereum} />
            <CopyableAddress label="Solana" address={cryptoAddresses.solana} />
            <CopyableAddress label="Kaspa" address={cryptoAddresses.kaspa} />
          </CryptoAddresses>

          <form onSubmit={handleCryptoSubmit}>
            <FormGroup>
              <Label htmlFor="cryptoType">Cryptocurrency Used</Label>
              <Select id="cryptoType" value={cryptoType} onChange={(e) => setCryptoType(e.target.value)} required>
                <option value="" disabled>Select Cryptocurrency</option>
                <option value="ethereum">Ethereum</option>
                <option value="solana">Solana</option>
                <option value="kaspa">Kaspa</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="transactionHash">Transaction Hash</Label>
              <Input id="transactionHash" value={transactionHash} onChange={(e) => setTransactionHash(e.target.value)} placeholder="Enter transaction hash" required />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" required />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="platform">Contact Platform</Label>
              <Select id="platform" value={platform} onChange={(e) => setPlatform(e.target.value)} required>
                <option value="" disabled>Select Platform</option>
                <option value="X">X</option>
                <option value="Telegram">Telegram</option>
                <option value="Email">Email</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="username">Username/Handle</Label>
              <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Enter your username" required />
            </FormGroup>

            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Processing...' : 'Submit Payment Details'}
            </Button>
          </form>
        </>
      ) : (
        <Button onClick={handleCardPayment} disabled={isLoading}>
          {isLoading ? 'Processing...' : 'Pay with Card'}
        </Button>
      )}

      <Message show={successMessage}>
        Payment submitted successfully! We'll contact you soon.
        <br />
        <ContactInfo>Our Email is {process.env.REACT_APP_CONTACT_EMAIL || 'Modmedia@lavabit.com'}</ContactInfo>
        <strong>Redirecting to homepage in 5 seconds...</strong>
      </Message>

      <Message show={!!errorMessage} error>
        {errorMessage || 'Error processing payment. Please try again.'}
      </Message>
    </CheckoutContainer>
  );
};

export default Checkout;